/** A faithful mock of the calculator's first screen (apps/solar-frontend), rendered from unsaved brand settings so
 * the preview follows every keystroke. Uses the widget's own variables (--sq-*). */
import { CheckCircle2, MapPin } from "lucide-react";
import { useEffect, type CSSProperties } from "react";
import type { S } from "../api/client";
import { onColor } from "../lib/color";

const SYSTEM_FONTS = /^(system-ui|-apple-system|inter|georgia|arial|helvetica|times new roman|segoe ui|sans-serif|serif)$/i;

/** Loads the brand font from Google Fonts for the preview, as the widget does. */
function useFont(fontFamily: string) {
  useEffect(() => {
    const first = fontFamily.split(",")[0]?.trim().replace(/^['"]|['"]$/g, "");
    if (!first || SYSTEM_FONTS.test(first)) return;
    const id = `font-${first.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first)}:wght@400;600;700&display=swap`;
    document.head.appendChild(link);
  }, [fontFamily]);
}

export function WidgetPreview({ brand }: { brand: S["BrandSettings"] }) {
  useFont(brand.font_family);
  const c = brand.colors;
  const style = {
    "--sq-primary": c.primary,
    "--sq-secondary": c.secondary,
    "--sq-accent": c.accent,
    "--sq-bg": c.background,
    "--sq-text": c.text,
    "--sq-font": brand.font_family || "Inter, system-ui, sans-serif",
    "--sq-on-primary": onColor(c.primary),
  } as CSSProperties;
  const steps = brand.flow.steps.length;
  return (
    <div className="wp" style={style}>
      <div className="wp-head">
        {brand.logo_url ? <img src={brand.logo_url} alt="" /> : <span className="wp-company">{brand.company_name}</span>}
      </div>
      <div className="wp-progress">
        {Array.from({ length: steps }, (_, i) => (
          <span key={i} className={i === 0 ? "on" : undefined} />
        ))}
      </div>
      <div className="wp-body">
        <h2 className="wp-title">{brand.texts.headline || "See what solar saves you"}</h2>
        {brand.texts.subheadline && <p className="wp-lead">{brand.texts.subheadline}</p>}
        <div className="wp-input">
          <MapPin />
          <span>Enter your home address</span>
        </div>
        <div className="wp-btn">{brand.texts.call_to_action || "Get my quote"}</div>
        <div className="wp-trust">
          <span>
            <CheckCircle2 /> Satellite imagery of your roof
          </span>
          <span>
            <CheckCircle2 /> Instant, no site visit
          </span>
          <span>
            <CheckCircle2 /> Free, no obligation
          </span>
        </div>
        <div className="wp-cards">
          <div className="wp-card">
            <small>Bill today</small>
            <b>$182/mo</b>
          </div>
          <div className="wp-card">
            <small>With solar</small>
            <b className="accent">$24/mo</b>
          </div>
          <div className="wp-card">
            <small>{brand.flow.show_financing ? "Financing from" : "System"}</small>
            <b>{brand.flow.show_financing ? "$119/mo" : "8.4 kW"}</b>
          </div>
        </div>
      </div>
      <div className="wp-foot">
        {[brand.contact.phone, brand.contact.email].filter(Boolean).join(" · ") || "Your contact details appear here"}
      </div>
    </div>
  );
}
